import { lazyOpenBizChatSidePane } from 'owa-bizchat-shared';
import {
    setWindowMailboxInfo,
    deleteWindowMailboxInfo,
} from 'owa-bizchat-shared/lib/utils/popoutWindowMailboxInfoMap';
import { resetPostMessageHandler } from 'owa-bizchat-shared/lib/utils/PostMessageToFrameHandler';
import { isBizchatSidepaneEnabled } from 'owa-bizchat-shared/lib/utils/isBizchatSidepaneEnabled';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { useResizeObserver } from 'owa-react-hooks/lib/useResizeObserver';
import { trace } from 'owa-trace';
import React from 'react';
import { useState } from 'owa-react';
import {
    isCopilotChatDrawerShown,
    setAppCanvasWidthMutator,
    removeCopilotChatDrawerState,
} from 'owa-copilot-chat-drawer-store';
import type { OpenBizChatSidePaneParams } from 'owa-bizchat-types';
import type { MailboxInfo } from 'owa-client-types';

export interface UseCopilotChatInPopoutResult {
    /** Whether the chat drawer popout feature is enabled */
    isChatDrawerInPopoutFeatureEnabled: boolean;
    /** Ref to attach to the popout canvas container for resize observation */
    popoutCanvasRef: React.RefObject<HTMLDivElement>;
    /** Whether the Copilot chat drawer should be rendered */
    shouldShowCopilotChatDrawer: boolean;
    /** Whether the Copilot chat drawer should be visible */
    isVisibleCopilotChatDrawer: boolean;
}

export function useCopilotChatInPopout(
    /**
     * Mailbox info of the current user. Undefined will not show the chat drawer, but
     * is required for hook rules, in the case of projection projection window landing
     * in a weird state where it does not have a state to derive mailboxInfo from.
     */
    mailboxInfo: MailboxInfo | undefined,
    targetWindow: Window,
    /**
     * Optional parameters to automatically open the BizChat side pane upon loading the popout
     * window. If not provided, the BizChat side pane will not open automatically.
     */
    openBizChatParams?: OpenBizChatSidePaneParams
): UseCopilotChatInPopoutResult {
    const isChatDrawerInPopoutFeatureEnabled = isGlobalFeatureEnabled('mon-copilot-bizchat-popout');
    const popoutCanvasRef = React.useRef<HTMLDivElement>(null);
    const copilotChatDrawerShown = isCopilotChatDrawerShown(targetWindow);

    // State to track if mos proxy is ready (needed for popout window communication with BizChat iframe)
    const [mosProxyReady, setMosProxyReady] = useState(
        (targetWindow as any).__mosProxyReady === true || targetWindow.opener === null
    );

    const isBizchatSidepaneEnabledValue = isBizchatSidepaneEnabled(mailboxInfo);
    const isVisibleCopilotChatDrawer =
        !!mailboxInfo && copilotChatDrawerShown && isBizchatSidepaneEnabledValue !== false;
    const shouldShowCopilotChatDrawer =
        !!mailboxInfo &&
        isChatDrawerInPopoutFeatureEnabled &&
        copilotChatDrawerShown &&
        isBizchatSidepaneEnabledValue !== false &&
        mosProxyReady;

    // Inform chat drawer of changes to width to allow it to resize appropriately
    const updatePopoutCanvasWidth = React.useCallback(
        (rect: DOMRectReadOnly) => {
            if (isChatDrawerInPopoutFeatureEnabled) {
                setAppCanvasWidthMutator(targetWindow, rect.width);
            }
        },
        [isChatDrawerInPopoutFeatureEnabled, targetWindow]
    );

    const handleMosProxyReady = React.useCallback((messageEvent: MessageEvent) => {
        if (messageEvent.data?.type === 'mos-proxy-ready') {
            setMosProxyReady(true);
        }
    }, []);

    // Wrap setter in useMemo to satisfy forbid-react-set-state-and-mutators-inside-specific-hooks
    const notifyMosProxyReady = React.useMemo(() => {
        return () => {
            if (!mosProxyReady) {
                setMosProxyReady(true);
            }
        };
    }, [mosProxyReady]);

    useResizeObserver('CopilotChatDrawerInPopouts', popoutCanvasRef, updatePopoutCanvasWidth);

    React.useEffect(() => {
        if (!isChatDrawerInPopoutFeatureEnabled) {
            return;
        }
        if (mailboxInfo) {
            setWindowMailboxInfo(targetWindow, mailboxInfo);
        }

        return () => {
            deleteWindowMailboxInfo(targetWindow);
            if (isChatDrawerInPopoutFeatureEnabled) {
                removeCopilotChatDrawerState(targetWindow);

                // Clean up BizChat message handler state when the popout window closes.
                // This is intentionally here (and not in M365ChatContainer's cleanup) so that
                // chatContext survives the drawer closing while the popout is still open.
                if (mailboxInfo) {
                    resetPostMessageHandler(mailboxInfo, targetWindow);
                }
            }
        };
    }, [isChatDrawerInPopoutFeatureEnabled, mailboxInfo, targetWindow]);

    // Separate effect to handle mos proxy readiness, avoiding race condition where
    // mos-proxy.js dispatches the ready event before the listener is registered.
    React.useEffect(() => {
        if (!isChatDrawerInPopoutFeatureEnabled) {
            return;
        }

        // Check if mos proxy is already ready before registering the listener
        if ((targetWindow as any).__mosProxyReady === true || targetWindow.opener === null) {
            notifyMosProxyReady();
            return;
        }

        targetWindow.addEventListener('message', handleMosProxyReady);

        return () => {
            targetWindow.removeEventListener('message', handleMosProxyReady);
        };
    }, [
        isChatDrawerInPopoutFeatureEnabled,
        targetWindow,
        handleMosProxyReady,
        notifyMosProxyReady,
    ]);

    // This useEffect is responsible for automatically opening the BizChat side pane as soon as the popout is ready
    React.useEffect(() => {
        if (isChatDrawerInPopoutFeatureEnabled && mosProxyReady && openBizChatParams) {
            lazyOpenBizChatSidePane.importAndExecute({
                ...openBizChatParams,
                targetWindow,
            });
        }
    }, [
        targetWindow,
        isChatDrawerInPopoutFeatureEnabled,
        mosProxyReady,
        openBizChatParams,
        mailboxInfo,
    ]);

    trace.info(
        `BizChat in popout drawer state:
            shouldShowCopilotChatDrawer: ${shouldShowCopilotChatDrawer},
            isCopilotChatDrawerShown: ${copilotChatDrawerShown}
            `,
        'iframeWrapper'
    );

    return {
        isChatDrawerInPopoutFeatureEnabled,
        popoutCanvasRef,
        shouldShowCopilotChatDrawer,
        isVisibleCopilotChatDrawer,
    };
}
